// skipcq: JS-W1028
import { useCallback, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import { object, string } from 'yup';

import mapFirebaseAuthError from '../../utils/mapFirebaseAuthError';
import {
  sendPasswordResetForEmail,
  signInWithEmailPasswordFlow,
  signInWithGoogle,
} from '../../utils/firebase';

const loginSchema = object({
  loginEmail: string().email('Enter a valid email').required('Email is required'),
  loginPassword: string().required('Password is required'),
});

const loginInitialValues = {
  loginEmail: '',
  loginPassword: '',
};

const RESET_INVALID_EMAIL_MESSAGE = 'Enter a valid email address.';
const RESET_GENERIC_FAILURE_MESSAGE =
  'Something went wrong. Please try again in a moment.';

function passwordResetBannerMessage(error) {
  if (error?.code === 'functions/invalid-argument') {
    return RESET_INVALID_EMAIL_MESSAGE;
  }
  return RESET_GENERIC_FAILURE_MESSAGE;
}

function ForgotPasswordLink({ loginEmail, onForgot }) {
  const handleClick = useCallback(() => {
    onForgot(loginEmail);
  }, [loginEmail, onForgot]);

  return (
    <Link
      component="button"
      type="button"
      variant="body2"
      underline="always"
      onClick={handleClick}
    >
      Forgot password?
    </Link>
  );
}

export default function LoginWelcomePane() {
  const [loginError, setLoginError] = useState('');
  const [resetBanner, setResetBanner] = useState(null);

  const handleDismissResetBanner = useCallback(() => {
    setResetBanner(null);
  }, []);

  const handleForgotPassword = useCallback(async (emailForReset) => {
    const trimmed = emailForReset?.trim?.() || '';
    if (!trimmed) {
      setResetBanner({
        message: 'Enter your email in the sign-in box, then tap Forgot.',
        severity: 'warning',
      });
      return;
    }
    setResetBanner(null);
    try {
      await sendPasswordResetForEmail(trimmed);
      setResetBanner({
        message:
          'If an account exists for that email, a reset link has been sent.',
        severity: 'success',
      });
    } catch (error) {
      setResetBanner({
        message: passwordResetBannerMessage(error),
        severity: 'error',
      });
    }
  }, []);

  const handleGoogleSignIn = useCallback(async () => {
    setLoginError('');
    try {
      await signInWithGoogle();
    } catch (error) {
      setLoginError(mapFirebaseAuthError(error));
    }
  }, []);

  const formik = useFormik({
    initialValues: loginInitialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, helpers) => {
      setLoginError('');
      try {
        await signInWithEmailPasswordFlow(
          values.loginEmail.trim(),
          values.loginPassword,
        );
      } catch (error) {
        setLoginError(mapFirebaseAuthError(error));
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: { xs: 'stretch', md: 'center' },
        py: { xs: 3, md: 4 },
        px: { xs: 2, sm: 4 },
      }}
    >
      <Paper
        component="form"
        onSubmit={formik.handleSubmit}
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 400,
          p: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" align="center" gutterBottom sx={{ mb: 2 }}>
          Welcome back!
        </Typography>

        <Stack spacing={2}>
          {loginError ? <Alert severity="error">{loginError}</Alert> : null}
          {resetBanner ? (
            <Alert
              severity={resetBanner.severity}
              onClose={handleDismissResetBanner}
            >
              {resetBanner.message}
            </Alert>
          ) : null}
          <TextField
            name="loginEmail"
            label="Email"
            type="email"
            autoComplete="username"
            value={formik.values.loginEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.loginEmail &&
              Boolean(formik.errors.loginEmail)
            }
            helperText={
              formik.touched.loginEmail && formik.errors.loginEmail
                ? formik.errors.loginEmail
                : ' '
            }
            margin="dense"
            fullWidth
          />
          <TextField
            name="loginPassword"
            label="Password"
            type="password"
            autoComplete="current-password"
            value={formik.values.loginPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.loginPassword &&
              Boolean(formik.errors.loginPassword)
            }
            helperText={
              formik.touched.loginPassword && formik.errors.loginPassword
                ? formik.errors.loginPassword
                : ' '
            }
            margin="dense"
            fullWidth
          />

          <Box>
            <ForgotPasswordLink
              loginEmail={formik.values.loginEmail}
              onForgot={handleForgotPassword}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={formik.isSubmitting}
            fullWidth
          >
            Log in
          </Button>

          <Button
            type="button"
            variant="outlined"
            size="large"
            fullWidth
            onClick={handleGoogleSignIn}
          >
            Sign in with Google
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
